import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "@/lib/lang";
import { SIGN_IN_URL } from "@/content";
import { LogoMark } from "./Logo";
import { Eyebrow, MaskLines, Reveal } from "./motion";

const MemberCard = ({ member, ownerNo }) => {
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rx = useSpring(useTransform(my, [0, 1], [14, -14]), { stiffness: 140, damping: 16 });
  const ry = useSpring(useTransform(mx, [0, 1], [-18, 18]), { stiffness: 140, damping: 16 });
  const sheen = useTransform(mx, [0, 1], ["0%", "100%"]);
  const bg = useTransform(sheen, (s) => `linear-gradient(115deg, transparent calc(${s} - 25%), rgba(255,255,255,0.55) ${s}, transparent calc(${s} + 25%))`);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };

  return (
    <div className="[perspective:1200px] py-6" onMouseMove={onMove} onMouseLeave={() => { mx.set(0.5); my.set(0.5); }} data-testid="member-card">
      <motion.div
        initial={{ rotateX: 30, rotateZ: -6, opacity: 0, y: 60 }}
        whileInView={{ rotateX: 0, rotateZ: -4, opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.15 }}
        transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
        className="mx-auto w-full max-w-[460px]"
      >
        <motion.div
          style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
          className="relative aspect-[1.586/1] w-full overflow-hidden rounded-[24px] bg-[linear-gradient(135deg,#0E8286_0%,#1BA3A6_40%,#5CC9C6_75%,#A8E6E2_100%)] p-6 md:p-8 text-white shadow-[0_50px_80px_-40px_rgba(14,95,115,0.7)]"
          dir="ltr"
        >
          <LogoMark className="absolute -end-14 -bottom-10 h-[115%] w-auto opacity-[0.12] text-white" />
          <motion.div style={{ background: bg }} className="pointer-events-none absolute inset-0 mix-blend-overlay" />
          <div className="relative flex h-full flex-col justify-between" style={{ transform: "translateZ(40px)" }}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2"><LogoMark className="h-8 w-8" /><span className="font-display text-2xl tracking-[0.2em]">OGEN</span></div>
              <span className="text-[11px] uppercase tracking-[0.3em]">{member}</span>
            </div>
            <div className="h-9 w-12 rounded-md bg-[linear-gradient(135deg,#ffffff,#cdeeee)] border border-white/40" />
            <div className="flex items-end justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-[0.3em] opacity-80">Tel Aviv Port</p>
                <p className="mt-1 font-display text-xl md:text-2xl" dir="auto">{ownerNo}</p>
              </div>
              <p className="font-display italic text-lg">2027</p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export const Club = () => {
  const { t } = useLang();
  return (
    <section id="club" className="relative bg-[#EEF8F7] py-24 md:py-36 overflow-hidden" data-testid="club-section">
      <div className="mx-auto max-w-[1440px] px-5 md:px-10 grid gap-16 lg:grid-cols-12 lg:items-center">
        <div className="lg:col-span-6">
          <Eyebrow>{t.club.eyebrow}</Eyebrow>
          <h2 className="mt-6 font-display text-[clamp(2.6rem,5vw,4.8rem)] leading-[0.98] text-[var(--ink)]">
            <MaskLines key={t.club.title} lines={[t.club.title]} />
          </h2>
          <Reveal><p className="mt-7 max-w-lg text-[15px] leading-relaxed text-[var(--ink-soft)]">{t.club.body}</p></Reveal>
          <ul className="mt-10 space-y-0">
            {t.club.perks.map((p, i) => (
              <Reveal key={i} delay={i * 0.08} className="border-t border-[#CFE6E6] py-6 grid grid-cols-[40px_1fr] gap-3" data-testid={`club-perk-${i}`}>
                <span className="text-[12px] tabular-nums text-[var(--sea)] pt-2">0{i + 1}</span>
                <div>
                  <h3 className="font-display text-2xl md:text-[28px] leading-tight text-[var(--ink)]">{p.title}</h3>
                  <p className="mt-2 text-[15px] leading-relaxed text-[var(--ink-soft)]">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-6">
          <MemberCard member={t.club.member} ownerNo={t.club.ownerNo} />
          <Reveal className="mx-auto mt-12 max-w-[460px] text-center">
            <a href={SIGN_IN_URL} target="_blank" rel="noopener noreferrer" className="btn-ink" data-testid="club-signin-link">
              {t.club.signIn}<ArrowUpRight className="h-4 w-4" />
            </a>
            <p className="mt-5 text-[13px] leading-relaxed text-[var(--ink-soft)]">{t.club.note}</p>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
